import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAsync } from 'react-async';
import { useTranslation } from 'react-i18next';
import qs from 'qs';
import KoobLoadingOverlay from '@koob/ui/src/components/KoobLoadingOverlay';
import { AdminBookingStateEnum } from '@koob/enums';
import { get, decodeId } from '@koob/utils';
import TripExport from './index';

// Public (unauthenticated) travel diary endpoints, addressed by the trip's
// share token (KD-3503) — same shape as the authenticated api modules, kept
// here so this package never imports from either app (US-025).
const getSharedTravelDiary = ({ shareToken }) =>
  get(`/api/internal/travel-diaries/${shareToken}`);

const getSharedTravelDiarySettings =
  (shareToken) =>
  ({ filters, skip } = {}) => {
    if (skip) {
      return Promise.resolve(null);
    }
    return get(
      `/api/internal/travel-diaries/${shareToken}/settings?${qs.stringify(filters)}`,
    );
  };

const getSharedHotel = ({ shareToken, hotelId, locale }) => {
  if (!hotelId) {
    return Promise.resolve(null);
  }
  return get(
    `/api/internal/travel-diaries/${shareToken}/hotels/${hotelId}?locale=${locale ?? 'en'}`,
  );
};

const getSharedTravelDiaryContents = ({ shareToken, locale }) =>
  get(
    `/api/internal/travel-diaries/${shareToken}/contents?${qs.stringify({ locale })}`,
  );

const getSharedPricingSummary = (shareToken) => () =>
  get(`/api/internal/travel-diaries/${shareToken}/pricing-summary`);

const getSharedOrganizationTheme = (shareToken) => () =>
  get(`/api/internal/travel-diaries/${shareToken}/theme`);

const getSharedExperience =
  (shareToken) =>
  ({ experienceId, locale }) => {
    if (!experienceId) {
      return Promise.resolve(null);
    }
    return get(
      `/api/internal/travel-diaries/${shareToken}/experiences/${experienceId}?locale=${locale ?? 'en'}`,
    );
  };

const getLanguages = () => get('/api/v1/languages');

// Public travel diary (KD-3503): renders the same diary as the authenticated
// export page, but everything is fetched through the unauthenticated
// /travel-diaries/:shareToken endpoints so end travellers only need the link.
export default function PublicTravelDiary() {
  const { shareToken } = useParams();
  const { t } = useTranslation('trips');

  const getExperience = useMemo(
    () => getSharedExperience(shareToken),
    [shareToken],
  );

  const [experiences, setExperiences] = useState([]);
  const [experiencesLoading, setExperiencesLoading] = useState(false);

  const [hotels, setHotels] = useState([]);
  const [hotelsLoading, setHotelsLoading] = useState(false);

  const [langSelected, setLangSelected] = useState(null);
  const [showLanguageModal, setShowLanguageModal] = useState(true);

  // Stable identity: react-async reloads whenever promiseFn changes.
  const getTravelDiarySettings = useMemo(
    () => getSharedTravelDiarySettings(shareToken),
    [shareToken],
  );
  const getPricingSummary = useMemo(
    () => getSharedPricingSummary(shareToken),
    [shareToken],
  );
  const getOrganizationTheme = useMemo(
    () => getSharedOrganizationTheme(shareToken),
    [shareToken],
  );

  const { data: diaryData, isLoading: isLoadingDiary } = useAsync({
    promiseFn: getSharedTravelDiary,
    shareToken,
  });

  const { data: pricingData } = useAsync({
    promiseFn: getPricingSummary,
    watch: shareToken,
  });
  const trip = diaryData?.data?.trip;
  const tourOperator = diaryData?.data?.tourOperator;
  const countries = trip?.countries;

  const { data: contentsData } = useAsync({
    promiseFn: getSharedTravelDiaryContents,
    shareToken,
    locale: langSelected?.value,
    watch: `${shareToken}|${langSelected?.value ?? ''}`,
  });

  const { data: languagesData } = useAsync({
    promiseFn: getLanguages,
  });
  const locales = languagesData?.data?.map(({ name, description }) => ({
    value: name,
    label: name.toUpperCase(),
    alpha2: description.toLowerCase(),
  }));

  const experienceIds = [
    ...new Set(trip?.tripDays?.map((day) => day.experienceId)?.filter(Boolean)),
  ];
  const hotelIds = [
    ...new Set(trip?.tripDays?.map((day) => day.hotelId)?.filter(Boolean)),
  ];

  useEffect(() => {
    if (experienceIds && experienceIds.length > 0) {
      setExperiencesLoading(true);

      const promises = experienceIds.map(async (experienceId) => {
        const experience = await getExperience({
          experienceId,
          locale: langSelected?.value,
        });
        return experience.data;
      });

      Promise.all(promises).then((res) => {
        setExperiences(res);
        setExperiencesLoading(false);
      });
    }

    if (hotelIds && hotelIds.length > 0) {
      setHotelsLoading(true);

      const promises = hotelIds.map(async (hotelId) => {
        const hotel = await getSharedHotel({
          shareToken,
          hotelId,
          locale: langSelected?.value,
        });
        return {
          ...hotel.data,
          id: decodeId(hotelId).toString(),
        };
      });

      Promise.all(promises).then((res) => {
        setHotels(res);
        setHotelsLoading(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experienceIds?.length, hotelIds?.length, langSelected]);

  const images = experiences?.reduce((acc, experience) => {
    if (experience.pictures) {
      experience.pictures.forEach((image) => {
        acc.push(image);
      });
    }
    return acc;
  }, []);

  const loading = isLoadingDiary || experiencesLoading || hotelsLoading;

  // Same cancelled-extras filtering as the authenticated export page.
  const { bookedExtraIds, activeExtraIds } = useMemo(() => {
    const bookedExtraIds = new Set();
    const activeExtraIds = new Set();
    trip?.bookings?.forEach((booking) =>
      booking?.experiencesBooked?.forEach((experienceBooked) =>
        experienceBooked?.extras?.forEach((extra) => {
          bookedExtraIds.add(extra.id);
          if (extra.state !== AdminBookingStateEnum.CANCELLED) {
            activeExtraIds.add(extra.id);
          }
        }),
      ),
    );
    return { bookedExtraIds, activeExtraIds };
  }, [trip]);

  const filteredTrip = useMemo(() => {
    if (!trip) return trip;
    const isCancelledOption = (option) => {
      const optionId = option.optionId ?? option.id;
      return bookedExtraIds.has(optionId) && !activeExtraIds.has(optionId);
    };
    return {
      ...trip,
      globalOptions: trip.globalOptions?.filter(
        (option) => !isCancelledOption(option),
      ),
      tripDays: trip.tripDays?.map((day) => ({
        ...day,
        options: day.options?.filter((option) => !isCancelledOption(option)),
      })),
    };
  }, [trip, bookedExtraIds, activeExtraIds]);

  return (
    <>
      {loading ? (
        <KoobLoadingOverlay
          title={t('export.loading.title')}
          description={t('export.loading.description')}
        />
      ) : (
        <TripExport
          trip={filteredTrip}
          countries={countries}
          tourOperator={tourOperator}
          images={images}
          experiences={experiences}
          hotels={hotels}
          locales={locales}
          getTravelDiarySettings={getTravelDiarySettings}
          getExperience={getExperience}
          langSelected={langSelected}
          setLangSelected={setLangSelected}
          showLanguageModal={showLanguageModal}
          setShowLanguageModal={setShowLanguageModal}
          shareUrl={window.location.href}
          travelDiaryContents={contentsData?.data ?? []}
          pricing={pricingData?.data}
          getOrganizationTheme={getOrganizationTheme}
        />
      )}
    </>
  );
}
